import { useState } from 'react';
import { Card, Checkbox, Button, Collapse, Tag } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
    RiskSymbolCell,
    ActionLinkCell,
    SimpleTextCell,
} from '@client/components/ui/table/TableCells';
import ItemsWithPopover from '@client/components/ui/table/ItemWithPopover';
import { ProductTypeLegend } from '@client/components/ui/productLegends';
import TruncatedText from '@client/components/ui/TruncatedText';
import externalLink from '../../../../../../img/external-link.svg';

const { Panel } = Collapse;

// Simple table component matching the design
const SimpleTable = ({ headers, data, emptyMessage }) => {
    if (!data || data.length === 0) {
        return emptyMessage ? <p className="text-gray-500 text-sm">{emptyMessage}</p> : null;
    }

    return (
        <div className="w-full">
            {/* Header row */}
            <div className="flex bg-gray-100 rounded-t">
                {headers.map((header, index) => (
                    <div
                        key={index}
                        className={`py-3 px-4 font-medium text-gray-700 ${
                            index === 0 ? 'flex-1' : 'w-32 text-left'
                        }`}
                    >
                        {header}
                    </div>
                ))}
            </div>
            {/* Data rows */}
            {data.map((row, rowIndex) => (
                <div
                    key={rowIndex}
                    className="flex border-b border-gray-100 last:border-b-0"
                >
                    {row.map((cell, cellIndex) => (
                        <div
                            key={cellIndex}
                            className={`py-3 px-4 ${
                                cellIndex === 0 ? 'flex-1' : 'w-32 text-left'
                            }`}
                        >
                            {cell || '-'}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

const TreatmentCard = ({ record, isSelected, onSelect }) => {
    const { t } = useTranslation();
    const [activeKeys, setActiveKeys] = useState([]);

    // Toggle all panels
    const toggleAllPanels = () => {
        if (activeKeys.length > 0) {
            setActiveKeys([]);
        } else {
            const allKeys = ['risks_delays', 'delays', 'enemies', 'toxicity'];
            setActiveKeys(allKeys);
        }
    };

    // Get allergens from products
    const getAllergens = () => {
        const allergens = [];
        record.treatment_products?.forEach((tp) => {
            tp?.product?.allergens?.forEach((allergen) => {
                if (allergen?.name && !allergens.find((a) => a.id === allergen.id)) {
                    allergens.push(allergen);
                }
            });
        });
        return allergens;
    };

    // Get enemies with ratings
    const getEnemiesWithRatings = () => {
        const enemies = [];
        record.treatment_enemies?.forEach((te) => {
            if (te?.enemy_rating_ARLA) {
                enemies.push({
                    id: te.enemy?.id || te.id,
                    name: te.enemy?.french_name || te.enemy?.english_name || te.enemy?.latin_name || te.enemy?.display_name,
                    rating: te.enemy_rating_ARLA?.name,
                    rating_display: te.enemy_rating_ARLA?.client_display !== 'False' ? te.enemy_rating_ARLA?.client_display : null,
                });
            }
        });
        return enemies;
    };

    // Get toxicity to useful species from products
    const getToxicityToUsefulSpecies = () => {
        const toxicityData = [];
        record.treatment_products?.forEach((tp) => {
            tp?.product?.toxicity_to_non_target_species?.forEach((tox) => {
                if (tox?.names_of_useful_species) {
                    toxicityData.push({
                        id: tox.id,
                        species: tox.names_of_useful_species?.name || tox.names_of_useful_species?.french_name,
                        level: tox.toxicity_level?.name,
                        item_mentioned: tox.item_mentioned?.name,
                        comment: tox.comment,
                    });
                }
            });
        });
        return toxicityData;
    };

    const allergens = getAllergens();
    const enemiesWithRatings = getEnemiesWithRatings();
    const toxicityData = getToxicityToUsefulSpecies();

    // Helper to format delay display
    const formatDelay = (delay) => {
        if (!delay) return null;
        if (delay.name) return delay.name;
        if (delay.formatted_duration) return delay.formatted_duration;
        if (delay.display_value && delay.display_unit) {
            const unitLabels = { hours: 'h', days: 'j', weeks: 'sem' };
            return `${delay.display_value}${unitLabels[delay.display_unit] || delay.display_unit}`;
        }
        if (delay.duration_in_hours) {
            if (delay.duration_in_hours < 24) return `${delay.duration_in_hours}h`;
            return `${Math.round(delay.duration_in_hours / 24)} j`;
        }
        return delay.value;
    };

    // Prepare DS data for simple table
    const dsTableData = record.treatment_security_delays?.map((sd) => [
        sd.activity_associated_with_security_delay?.name || '-',
        formatDelay(sd.security_delay),
    ]) || [];

    // Prepare DAAR data for simple table
    const daarTableData = record.treatment_activity_associated_with_preharvest_waiting_times?.map((aw) => [
        aw.activity_associated_with_preharvest_waiting_time?.name || '-',
        formatDelay(aw.delay_before_harvest),
    ]) || [];

    // Prepare enemies data for simple table
    const enemiesTableData = enemiesWithRatings.map((e) => [
        e.name || '-',
        e.rating || '-',
    ]);

    // Prepare toxicity data for simple table
    const toxicityTableData = toxicityData.map((tox) => [
        tox.species || '-',
        tox.level || '-',
    ]);

    return (
        <Card
            className="treatment-card mb-4 shadow-sm hover:shadow-md transition-shadow"
        >
            {/* Card Header */}
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                    <Checkbox
                        checked={isSelected}
                        onChange={(e) => onSelect(record.id, e.target.checked)}
                    />
                    <ActionLinkCell
                        href={`/traitement/details/${record.id}`}
                        target="_blank"
                        className="btn btn--primary hover:text-[#000] gap-2"
                    >
                        <img src={externalLink} alt="External link" />
                        {t('treatment-search|Traitement complet')}
                    </ActionLinkCell>
                </div>
                <Button
                    type="primary"
                    shape="circle"
                    onClick={toggleAllPanels}
                    icon={activeKeys.length > 0 ? <MinusOutlined /> : <PlusOutlined />}
                    size="small"
                    className="bg-[#A0D911] hover:bg-[#7CB305] border-none"
                    style={{ minWidth: '32px', width: '32px', height: '32px' }}
                />
            </div>

            {/* Collapsible Sections */}
            <Collapse
                activeKey={activeKeys}
                onChange={setActiveKeys}
                ghost
                className="bg-transparent treatment-collapse"
                expandIconPosition="end"
            >
                {/* Risques et délais Section */}
                <Panel
                    header={
                        <div className="px-4 py-2 rounded font-medium">
                            {t('treatment-search|Risques et délais')}
                        </div>
                    }
                    key="risks_delays"
                    className="mb-2"
                >
                    <div className="space-y-6">
                        {/* Risk Indices and Symbols */}
                        <div className="flex gap-6 flex-col">
                            {/* Health Risk Index */}
                            <div className="flex items-center gap-2">
                                <span className="font-medium">IRS : {record?.health_risk_index ?? 'N/A'}</span>
                                <div className="flex gap-1 items-center">
                                    <RiskSymbolCell
                                        symbolData={record.symbols?.health?.acute_toxicity_symbol}
                                        alt="Acute toxicity"
                                    />
                                    <RiskSymbolCell
                                        symbolData={record.symbols?.health?.long_term_effect_symbol}
                                        alt="Long term effects"
                                    />
                                </div>
                            </div>

                            {/* Environment Risk Index */}
                            <div className="flex items-center gap-2">
                                <span className="font-medium">IRE : {record?.environment_risk_index ?? 'N/A'}</span>
                                <div className="flex gap-1 items-center">
                                    <RiskSymbolCell
                                        symbolData={record.symbols?.environment?.fishes_symbol}
                                        alt="Fish or daphnia"
                                    />
                                    <RiskSymbolCell
                                        symbolData={record.symbols?.environment?.birds_symbol}
                                        alt="Birds"
                                    />
                                    <RiskSymbolCell
                                        symbolData={record.symbols?.environment?.bees_symbol}
                                        alt="Bees"
                                    />
                                    <RiskSymbolCell
                                        symbolData={record.symbols?.environment?.persistence_symbol}
                                        alt="Persistence"
                                    />
                                    <RiskSymbolCell
                                        symbolData={record.symbols?.environment?.leaching_symbol}
                                        alt="Leaching potential"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Product Information */}
                        {record?.treatment_products?.length > 0 && (
                            <div className="mt-4">
                                <p className="text-[16px] font-bold mb-2">
                                    {t('treatment-search|Produit commercial ou série')}
                                </p>
                                {record.treatment_products.map((tp, index) => (
                                    <div key={index} className="flex flex-col gap-2 mb-3 p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="px-3 py-1 rounded-lg bg-[#F5F5F5] text-[#105235] font-bold text-[14px] underline">
                                                <TruncatedText text={tp?.product?.name || tp?.series?.name} maxLength={40} />
                                            </span>
                                            {tp?.selection_type === 'series' && (
                                                <ProductTypeLegend type="serie" />
                                            )}
                                            {tp?.product?.is_organic_approval && (
                                                <ProductTypeLegend type="biological" height={22} />
                                            )}
                                        </div>
                                        {/* Dose */}
                                        {(tp?.min_dose || tp?.max_dose) && (
                                            <div className="text-sm text-gray-600">
                                                <span className="font-medium">{t('treatment-search|Dose')} : </span>
                                                {tp.min_dose && tp.max_dose
                                                    ? `${tp.min_dose} - ${tp.max_dose}`
                                                    : tp.min_dose || tp.max_dose
                                                }
                                                {tp?.units?.name && ` ${tp.units.name}`}
                                            </div>
                                        )}
                                        {/* Active Substances for this product */}
                                        {tp?.product?.active_substances?.length > 0 && (
                                            <div className="mt-1">
                                                <ItemsWithPopover
                                                    items={tp.product.active_substances}
                                                    getItemName={(as) => {
                                                        const pesticidGroups = as?.active_substance?.pesticide_group_associations
                                                            ?.map((assoc) => assoc.pesticide_group?.name)
                                                            .filter((name) => name)
                                                            .join(', ');
                                                        return `${as?.active_substance?.name}${pesticidGroups ? ` (${pesticidGroups})` : ''}`;
                                                    }}
                                                    isNested={true}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Allergens */}
                        {allergens.length > 0 && (
                            <div className="mt-4">
                                <p className="text-[16px] font-bold mb-2">
                                    {t('treatment-search|Allergènes')}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {allergens.map((allergen, index) => (
                                        <Tag key={allergen.id || index}>
                                            {allergen.name}
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </Panel>

                {/* Délais Section */}
                <Panel
                    header={
                        <div className="px-4 py-2 rounded font-medium">
                            {t('treatment-search|Délais')}
                        </div>
                    }
                    key="delays"
                    className="mb-2"
                >
                    <div className="bg-white space-y-6">
                        {/* Security Delays (DS) */}
                        <div>
                            <p className="text-[16px] font-bold mb-3">
                                {t('treatment-search|Délais de sécurité (DS)')} :
                            </p>
                            {dsTableData.length > 0 ? (
                                <SimpleTable
                                    headers={[t('treatment-search|Activité'), 'DS']}
                                    data={dsTableData}
                                />
                            ) : (
                                <SimpleTextCell text={record?.security_delays_combined} />
                            )}
                        </div>

                        {/* DAAR */}
                        <div>
                            <p className="text-[16px] font-bold mb-3">
                                {t("treatment-search|Délais d'attente avant récolte (DAAR)")} :
                            </p>
                            {daarTableData.length > 0 ? (
                                <SimpleTable
                                    headers={[t('treatment-search|Activité'), 'DAAR']}
                                    data={daarTableData}
                                />
                            ) : (
                                <SimpleTextCell text={record?.delays_before_harvest_combined} />
                            )}
                        </div>

                        {/* Délai avant la pluie */}
                        {record?.delay_before_rain && (
                            <div>
                                <p className="text-[16px] font-bold mb-2">
                                    {t('treatment-search|Délais avant la pluie')}:
                                </p>
                                <p className="px-4">{record.delay_before_rain}</p>
                            </div>
                        )}

                        {/* Délai de rentrée */}
                        {record?.re_entry_deadline && (
                            <div>
                                <p className="text-[16px] font-bold mb-2">
                                    {t('treatment-search|Délai de rentrée')}:
                                </p>
                                <p className="px-4">{formatDelay(record.re_entry_deadline)}</p>
                            </div>
                        )}
                    </div>
                </Panel>

                {/* Ennemis Section */}
                {enemiesWithRatings.length > 0 && (
                    <Panel
                        header={
                            <div className="px-4 py-2 rounded font-medium">
                                {t('treatment-search|Ennemis')}
                            </div>
                        }
                        key="enemies"
                        className="mb-2"
                    >
                        <div className="bg-white">
                            <SimpleTable
                                headers={[t('treatment-search|Ennemi'), t('treatment-search|Efficacité ARLA')]}
                                data={enemiesTableData}
                            />
                        </div>
                    </Panel>
                )}

                {/* Toxicité sur les espèces utiles Section */}
                <Panel
                    header={
                        <div className="px-4 py-2 rounded font-medium">
                            {t('treatment-search|Toxicité sur les espèces utiles')}
                        </div>
                    }
                    key="toxicity"
                    className="mb-2"
                >
                    <div className="bg-white">
                        {toxicityTableData.length > 0 ? (
                            <SimpleTable
                                headers={[t('treatment-search|Espèce utile'), t('treatment-search|Niveau de toxicité')]}
                                data={toxicityTableData}
                            />
                        ) : (
                            <div className="space-y-4">
                                <p className="text-[16px] font-bold">{t("treatment-search|Selon l'étiquette")}:</p>
                                <p className="text-sm text-gray-600 px-4">
                                    {t('treatment-search|Aucune donnée de toxicité disponible')}
                                </p>
                            </div>
                        )}
                    </div>
                </Panel>
            </Collapse>
        </Card>
    );
};

export default TreatmentCard;
